import React, { useState } from "react";
import toast from "react-hot-toast";
import useStore from "../../store/useStore";
import { FaHeart, FaLock } from "react-icons/fa";

/**
 * WishlistButton — heart toggle button.
 * Props:
 *   productId  — number | string
 *   variantId  — number | string | null
 *   size       — 'sm' | 'md' | 'lg'
 *   className  — extra classes
 *   alwaysVisible — show even when not hovering (default false; ProductCard uses opacity-0 on parent)
 */
const WishlistButton = ({
  productId,
  variantId = null,
  size = "md",
  className = "",
}) => {
  const { isAuthenticated, isInWishlist, toggleWishlist, openAuthModal } =
    useStore();
  const [localLoading, setLocalLoading] = useState(false);

  const inWishlist = isInWishlist(productId);

  const sizes = {
    sm: { btn: "h-7 w-7", icon: "h-3.5 w-3.5" },
    md: { btn: "h-9 w-9", icon: "h-4.5 w-4.5" },
    lg: { btn: "h-11 w-11", icon: "h-6 w-6" },
  };
  const sz = sizes[size] || sizes.md;

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isAuthenticated) {
      toast("Please login to save items to wishlist", { icon: <FaLock /> });
      if (openAuthModal) openAuthModal();
      return;
    }

    // Capture state BEFORE async toggle so the toast message is correct
    const wasInWishlist = inWishlist;
    setLocalLoading(true);
    try {
      const result = await toggleWishlist(productId, variantId);
      if (result !== false) {
        wasInWishlist
          ? toast.success("Removed from wishlist", { icon: <FaHeart /> })
          : toast.success("Added to wishlist!", { icon: <FaHeart /> });
      }
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={localLoading}
      title={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
      className={`flex items-center justify-center rounded-full shadow-md transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 border-2
        ${
          inWishlist
            ? "bg-red-500 text-white border-red-600 hover:bg-red-600 scale-110"
            : "bg-white text-gray-400 border-red-300 hover:bg-red-50 hover:text-red-400"
        }
        ${sz.btn} ${className}`}
    >
      {localLoading ? (
        <svg
          className={`${sz.icon} animate-spin`}
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8z"
          />
        </svg>
      ) : (
        <svg
          className={sz.icon}
          fill={inWishlist ? "currentColor" : "none"}
          stroke={inWishlist ? "none" : "currentColor"}
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      )}
    </button>
  );
};

export default WishlistButton;
